import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useAnimationFrame,
  useReducedMotion,
} from "framer-motion";
import { sizeJitter } from "../data/skills";

const SkillChip = ({ item, idx }) => {
  const scale = sizeJitter(idx + 1);
  return (
    <motion.div
      whileHover={{ y: -6, scale: scale * 1.06 }}
      transition={{ type: "spring", stiffness: 280, damping: 18 }}
      style={{ transform: `scale(${scale})` }}
      className="group relative shrink-0 cursor-pointer"
      data-testid={`skill-chip-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="relative flex items-center gap-3 px-5 py-3.5 rounded-2xl glass border-white/10 group-hover:border-cyan-400/40 transition-colors duration-300 overflow-hidden">
        {/* Hover glow */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background:
              "radial-gradient(120px 80px at 30% 50%, rgba(0,240,255,0.18), transparent 70%)",
          }}
        />
        <item.Icon
          size={22}
          style={{ color: item.color }}
          className="shrink-0 drop-shadow-[0_0_12px_rgba(0,240,255,0.0)] group-hover:drop-shadow-[0_0_18px_rgba(0,240,255,0.45)] transition-[filter] duration-300"
        />
        <span className="text-sm font-medium text-slate-200 group-hover:text-white whitespace-nowrap font-display">
          {item.name}
        </span>
      </div>
    </motion.div>
  );
};

const MarqueeRow = ({ items, direction = -1, speed = 40, rowIndex = 0 }) => {
  // Build a duplicated track so the loop is seamless.
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const x = useMotionValue(0);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [halfWidth, setHalfWidth] = useState(0);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        setHalfWidth(trackRef.current.scrollWidth / 2);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [items]);

  useAnimationFrame((_t, delta) => {
    if (prefersReduced || hovered || dragging || !halfWidth) return;
    const move = direction * (speed * delta) / 1000;
    let next = x.get() + move;
    // wrap seamlessly
    if (next <= -halfWidth) next += halfWidth;
    if (next >= 0) next -= halfWidth;
    x.set(next);
  });

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: rowIndex * 0.12, ease: [0.22, 1, 0.36, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative overflow-hidden cursor-grab active:cursor-grabbing"
      data-testid={`skills-marquee-row-${rowIndex}`}
    >
      <motion.div
        ref={trackRef}
        style={{ x }}
        drag="x"
        dragConstraints={{ left: -Infinity, right: Infinity }}
        dragElastic={0.06}
        dragMomentum={false}
        onDragStart={() => setDragging(true)}
        onDragEnd={() => setDragging(false)}
        className="flex gap-4 w-max py-2 select-none"
      >
        {[...items, ...items].map((it, i) => (
          <SkillChip key={`${rowIndex}-${i}`} item={it} idx={i + rowIndex * 17} />
        ))}
      </motion.div>
    </motion.div>
  );
};

const SkillsMarquee = ({ rows }) => {
  // Row 1 left, Row 2 right, Row 3 left (alternate)
  const directions = [-1, 1, -1];
  // Slightly different speeds per row for organic depth
  const speeds = [38, 30, 44];

  // On mobile, slow everything down for legibility
  const [mobileFactor, setMobileFactor] = useState(1);
  useEffect(() => {
    const onResize = () =>
      setMobileFactor(window.innerWidth < 768 ? 0.65 : 1);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="relative space-y-5">
      {/* Edge fades */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-32 z-10"
        style={{
          background:
            "linear-gradient(to right, #030305 0%, rgba(3,3,5,0.7) 50%, transparent 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-32 z-10"
        style={{
          background:
            "linear-gradient(to left, #030305 0%, rgba(3,3,5,0.7) 50%, transparent 100%)",
        }}
      />

      {rows.map((row, i) => (
        <MarqueeRow
          key={i}
          items={row}
          direction={directions[i] ?? -1}
          speed={(speeds[i] ?? 36) * mobileFactor}
          rowIndex={i}
        />
      ))}
    </div>
  );
};

export default SkillsMarquee;
